import React, { useState } from 'react'
import { useCreateLeadMutation } from '../api/apiSlice'
import {
    Alert,
    Box,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    Link,
    Stack,
    TextField,
    Typography
} from '@mui/material'
import * as yup from 'yup'
import { Controller } from 'react-hook-form'
import { useStepForm } from '../form/steps/hooks'
import { NextStep } from '../form/components/NextStep'
import { Trans, useTranslation } from 'react-i18next'
import { lang } from '../../dom'
import { FormDefinition } from '../api/interfaces'
import Grid from '@mui/material/Unstable_Grid2'
import { SalesPersonSelect } from '../form/components/SalesPersonSelect'

interface EmailConfirmationProps {
    form: FormDefinition
    salesPersonKey: string | null
}

export const EmailConfirmation = (props: EmailConfirmationProps) => {
    const [triggerCreateLead, { status, error }] = useCreateLeadMutation()
    const schema = yup.object({
        companyName: yup.string().trim().required(),
        emailAddress: yup.string().trim().email().required(),
        firstName: yup.string().trim().required(),
        lastName: yup.string().trim().required(),
        authorizedToRepresent: yup.boolean().isTrue(),
        acceptPrivacyPolicy: yup.boolean().isTrue()
    })
    const { form, salesPersonKey } = props

    const { t } = useTranslation()

    const {
        control,
        handleSubmit,
        getValues,
        continueDisabled,
        formState,
        augmentedFieldProps,
    } = useStepForm('EmailConfirmation', schema, {}, false)


    const legalRequirementsError = !!formState.errors?.hasOwnProperty('authorizedToRepresent')
        || !!formState.errors?.hasOwnProperty('acceptPrivacyPolicy')

    const preSelectedSalesPersonId = (() => {
        if (!salesPersonKey || form.salesPersons.length === 0) {
            return null
        }
        return form.salesPersons.find((item) => item.shortKey.toLowerCase() === salesPersonKey)?.id
    })()

    const onSubmit = (data: Record<string, any>) => {
        const values = {
            ...data,
            url: document.location.toString(),
            language: lang,
            salesPerson: preSelectedSalesPersonId ? preSelectedSalesPersonId : (data.salesPerson ?? '')
        }
        triggerCreateLead(values)
    }

    if (status == 'fulfilled') {
        // TODO: add explainer text, and maybe a title
        return <>
            <Alert severity="success">{t('EmailConfirmation.doubleOptInStarted.success', getValues())}</Alert>
        </>
    }

    // TODO: add title, and maybe an introduction
    return <>
        <form onSubmit={handleSubmit(onSubmit)}>
            <Stack maxWidth={'sm'} spacing={2}>
                <Box>
                    <Typography component="h2" variant="h6" mb={1}>{t('EmailConfirmation.company.heading')}</Typography>
                    <Controller
                        name="companyName"
                        control={control}
                        defaultValue=""
                        render={({ field }) => <TextField
                            {...augmentedFieldProps(field)}
                            fullWidth={true}
                        />}
                    />
                </Box>

                <Box>
                    <Typography component="h2" variant="h6" mt={2}>{t('EmailConfirmation.personalData.heading')}</Typography>
                    <Typography variant="body2">{t('EmailConfirmation.personalData.introduction')}</Typography>
                </Box>

                <Box>
                    <Grid container columnSpacing={2}>
                        <Grid xs={6}>
                            <Controller
                                name="firstName"
                                defaultValue=""
                                control={control}
                                render={({ field }) => <TextField
                                    {...augmentedFieldProps(field)}
                                    fullWidth={true}
                                />}
                            />
                        </Grid>
                        <Grid xs={6}>
                            <Controller
                                name="lastName"
                                defaultValue=""
                                control={control}
                                render={({ field }) => <TextField
                                    {...augmentedFieldProps(field)}
                                    fullWidth={true}
                                />}
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Controller
                    name="emailAddress"
                    defaultValue=""
                    control={control}
                    render={({ field }) => <TextField
                        {...augmentedFieldProps(field)}
                        fullWidth={true}
                    />}
                />

                {!preSelectedSalesPersonId && form.salesPersons.length > 0 &&
                    <>
                        <Box mt={2}>
                            <Typography component="h2" variant="h6" mt={2}>
                                {t('EmailConfirmation.salesPerson.heading')}
                            </Typography>
                        </Box>
                        <Controller
                            name="salesPerson"
                            defaultValue=""
                            control={control}
                            render={({ field }) =>
                                <SalesPersonSelect
                                    {...augmentedFieldProps(field)}
                                    salesPersons={form.salesPersons}
                                />
                            }
                        />
                    </>
                }

                <FormControl
                    sx={{ m: 3 }}
                    component="fieldset"
                    variant="standard"
                    error={legalRequirementsError}
                >
                    <Typography component="legend" variant="h6" mt={2}>{t('EmailConfirmation.legalRequirements.label')}</Typography>
                    <FormGroup>
                        <Controller
                            control={control}
                            name={'authorizedToRepresent'}
                            defaultValue={false}
                            render={({ field }) =>
                                <FormControlLabel
                                    control={<Checkbox {...field}/>}
                                    label={augmentedFieldProps(field).label}
                                />
                            }
                        />
                        <Controller
                            control={control}
                            name={'acceptPrivacyPolicy'}
                            defaultValue={false}
                            render={({ field }) =>
                                <FormControlLabel
                                    control={<Checkbox {...field}/>}
                                    label={<Trans
                                        i18nKey="EmailConfirmation.acceptPrivacyPolicy.label"
                                        values={{ consolidatorName: form.consolidatorLegalName }}
                                        components={{
                                            privacyPolicyLink: <Link target="_blank" href={form.privacyPolicyUrl}/>
                                        }}
                                    />}
                                />
                            }
                        />
                    </FormGroup>
                    {legalRequirementsError &&
                        <FormHelperText>{t('EmailConfirmation.legalRequirements.error')}</FormHelperText>
                    }
                </FormControl>
                <Box mt={2}>
                    <NextStep
                        disabled={continueDisabled}
                        translationKey="EmailConfirmation.confirmButton.label"
                    />
                </Box>
            </Stack>
        </form>
    </>
}
